import React, { useState, useRef, useEffect } from "react";
import "../styles/skills.css";
import "../styles/myStats.css";
import { initialTree } from "./skillTreeData";

export default function Skills() {
  const nodeRefs = useRef({});

  const [selectedCoreId, setSelectedCoreId] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedLeafId, setSelectedLeafId] = useState(null);
  const [linePaths, setLinePaths] = useState([]);

  const handleSelect = (level, node) => {
    if (level === "core") {
      const isDeselect = selectedCoreId === node.id;
      setSelectedCoreId(isDeselect ? null : node.id);
      setSelectedBranchId(null);
      setSelectedLeafId(null);
    } else if (level === "branch") {
      const isDeselect = selectedBranchId === node.id;
      setSelectedBranchId(isDeselect ? null : node.id);
      setSelectedLeafId(null);
    } else if (level === "leaf") {
      setSelectedLeafId(selectedLeafId === node.id ? null : node.id);
    }
  };

  const getCenter = (element) => {
    if (!element || !element.getBoundingClientRect) return null;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const parentRect = document
      .querySelector(".tree-section")
      ?.getBoundingClientRect();
    if (!parentRect) return null;
    return {
      x: rect.left - parentRect.left + rect.width / 2,
      y: rect.top - parentRect.top + rect.height / 2,
    };
  };

  const createElbowPath = (x1, y1, x2, y2, radius = 20) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const horizontalMid = x1 + dx / 2;
    const verticalDirection = dy >= 0 ? 1 : -1;

    return `M ${x1} ${y1} H ${horizontalMid - radius} 
          A ${radius} ${radius} 0 0 ${
      verticalDirection > 0 ? 1 : 0
    } ${horizontalMid} ${y1 + radius * verticalDirection} 
          V ${y2 - radius * verticalDirection} 
          A ${radius} ${radius} 0 0 ${verticalDirection > 0 ? 0 : 1} ${
      horizontalMid + radius
    } ${y2} 
          H ${x2}`;
  };

  useEffect(() => {
    const fromCore = getCenter(nodeRefs.current[selectedCoreId]);
    const toBranch = getCenter(nodeRefs.current[selectedBranchId]);
    const toLeaf = getCenter(nodeRefs.current[selectedLeafId]);

    const getTypeFromId = (id) => {
      if (id?.startsWith("core-1") || id?.startsWith("arts")) return "arts";
      if (id?.startsWith("core-2") || id?.startsWith("tech")) return "tech";
      if (id?.startsWith("core-3") || id?.startsWith("meta")) return "meta";
      return "tech";
    };

    const newPaths = [];

    if (fromCore && toBranch) {
      newPaths.push({
        d: createElbowPath(fromCore.x, fromCore.y, toBranch.x, toBranch.y),
        type: getTypeFromId(selectedCoreId),
      });
    }

    if (toBranch && toLeaf) {
      newPaths.push({
        d: createElbowPath(toBranch.x, toBranch.y, toLeaf.x, toLeaf.y),
        type: getTypeFromId(selectedBranchId),
      });
    }

    setLinePaths(newPaths);
  }, [selectedCoreId, selectedBranchId, selectedLeafId]);

  const getName = () => {
    const core = initialTree.find((c) => c.id === selectedCoreId);
    const branch = core?.branches.find((b) => b.id === selectedBranchId);
    const leaf = branch?.leaves?.find((l) => l.id === selectedLeafId);
    return leaf?.name || branch?.name || core?.name || "";
  };

  const getDescription = () => {
    const core = initialTree.find((c) => c.id === selectedCoreId);
    const branch = core?.branches.find((b) => b.id === selectedBranchId);
    const leaf = branch?.leaves?.find((l) => l.id === selectedLeafId);
    return (
      leaf?.description ||
      branch?.description ||
      core?.description ||
      "Click a node to learn more about my skills."
    );
  };

  const selectedCore = initialTree.find((c) => c.id === selectedCoreId);
  const selectedBranch = selectedCore?.branches.find(
    (b) => b.id === selectedBranchId
  );

  return (
    <div className="skills-container">
      <div className="dialog-box dialog-box--tree">
        <div className="tree-scroll-wrapper">
          <div className="tree-section vertical-rows">
            <svg className="tree-lines" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gradient-mask-gradient">
                  <stop offset="0" stopColor="white" stopOpacity="0" />
                  <stop offset="0.35" stopColor="white" stopOpacity="1" />
                  <stop offset="0.65" stopColor="white" stopOpacity="1" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </linearGradient>

                <mask id="gradient-glow-mask">
                  <rect
                    className="mask-rect"
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill="url(#gradient-mask-gradient)"
                  />
                </mask>
              </defs>

              {linePaths.map((line, i) => (
                <React.Fragment key={i}>
                  <path d={line.d} className="line" />
                  <path
                    d={line.d}
                    className={`line-glow-masked line-glow-${line.type}`}
                    mask="url(#gradient-glow-mask)"
                  />
                </React.Fragment>
              ))}
            </svg>

            {[0, 1, 2].map((i) => {
              const core = initialTree[i];
              const branch = selectedCore?.branches?.[i];
              const leaf = selectedBranch?.leaves?.[i];

              return (
                <div key={core.id} className="tree-row">
                  <div
                    ref={(el) => (nodeRefs.current[core.id] = el)}
                    className={`skill-node skill-node--${core.name.toLowerCase()} ${
                      selectedCoreId === core.id && !selectedBranchId
                        ? "active glowing"
                        : selectedCoreId === core.id
                        ? "active"
                        : selectedCoreId
                        ? "greyed-out"
                        : ""
                    }`}
                    onClick={() => handleSelect("core", core)}
                  >
                    {core.name}
                  </div>

                  <div className="branch-column">
                    {branch ? (
                      <div
                        ref={(el) => (nodeRefs.current[branch.id] = el)}
                        className={`skill-node skill-node--${branch.id} ${core.name.toLowerCase()}-branch ${
                          selectedBranchId === branch.id && !selectedLeafId
                            ? "active glowing"
                            : selectedBranchId === branch.id
                            ? "active"
                            : selectedBranchId
                            ? "greyed-out"
                            : ""
                        }`}
                        onClick={() => handleSelect("branch", branch)}
                      >
                        {branch.name}
                      </div>
                    ) : (
                      <div className="skill-placeholder" />
                    )}
                  </div>

                  <div className="leaf-column">
                    {leaf ? (
                      <div
                        ref={(el) => (nodeRefs.current[leaf.id] = el)}
                        className={`skill-node skill-node--${leaf.id} ${core.name.toLowerCase()}-leaf ${
                          selectedLeafId === leaf.id ? "active glowing" : ""
                        }`}
                        onClick={() => handleSelect("leaf", leaf)}
                      >
                        {leaf.name}
                      </div>
                    ) : (
                      <div className="skill-placeholder" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="description-box">
        <h3>Skill: {getName()}</h3>
        <p>{getDescription()}</p>
        <span id="skillPrompt" className="dialog-prompt">▼</span>
      </div>
    </div>
  );
}
